import { useCallback, useEffect, useMemo, useState } from 'react';
import { resolveVideoEmbed } from '../utils';
import type { ResolvedVideo } from '../types';

export type VideoPlayerPhase = 'loading' | 'loaded' | 'error';

export interface UseVideoPlayerOptions {
  videoUrl: string;
  isOpen: boolean;
  autoplay: boolean;
  loadTimeoutMs: number;
}

export interface UseVideoPlayer {
  resolved: ResolvedVideo | null;
  phase: VideoPlayerPhase;
  handleLoad: () => void;
  handleError: () => void;
}

export function useVideoPlayer({
  videoUrl,
  isOpen,
  autoplay,
  loadTimeoutMs,
}: UseVideoPlayerOptions): UseVideoPlayer {
  const resolved = useMemo(
    () => resolveVideoEmbed(videoUrl, { autoplay }),
    [videoUrl, autoplay]
  );

  const [phase, setPhase] = useState<VideoPlayerPhase>(
    resolved ? 'loading' : 'error'
  );

  useEffect(() => {
    if (isOpen) setPhase(resolved ? 'loading' : 'error');
  }, [isOpen, resolved]);

  useEffect(() => {
    if (!isOpen || phase !== 'loading') return;
    const timer = window.setTimeout(() => setPhase('error'), loadTimeoutMs);
    return () => window.clearTimeout(timer);
  }, [isOpen, phase, loadTimeoutMs]);

  const handleLoad = useCallback(() => setPhase('loaded'), []);
  const handleError = useCallback(() => setPhase('error'), []);

  return { resolved, phase, handleLoad, handleError };
}
